import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useOfflineSync } from '../../hooks/useOfflineSync';
import { getRoleLabel } from '../../config/role-routes';
import { adminDashboardService, DashboardStats } from '../../services/admin-dashboard.service';
import { advanceService, Advance } from '../../services/advance.service';
import { StatCard } from '../../components/ui/StatCard';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(n || 0);
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Ahora';
  if (m < 60) return `Hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Hace ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `Hace ${d}d`;
  return new Date(ts).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
}

interface RoleDashboardProps {
  role: string;
}

function AdminGM({ role }: RoleDashboardProps) {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    adminDashboardService
      .getStats()
      .then(setStats)
      .catch((e) => setError(e.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <LoadingState message="Cargando dashboard…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!stats) return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon="🏗️" value={stats.activeProjects} label="Proyectos activos" />
        <StatCard
          icon="💰"
          value={formatCurrency(stats.monthlyRevenue)}
          label="Ingresos del mes"
        />
        <StatCard icon="👥" value={stats.totalClients} label="Clientes" />
        <StatCard icon="📊" value={`${stats.averageProgress}%`} label="Avance promedio" />
      </div>

      {(stats.alerts.overdueInvoices > 0 ||
        stats.alerts.pendingChangeOrders > 0 ||
        stats.alerts.staleProjects > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <p className="text-xs font-bold text-amber-800 mb-1.5">⚠️ Alertas del sistema</p>
          <div className="flex flex-wrap gap-3 text-xs text-amber-700">
            {stats.alerts.overdueInvoices > 0 && (
              <span>{stats.alerts.overdueInvoices} facturas vencidas</span>
            )}
            {stats.alerts.pendingChangeOrders > 0 && (
              <span>{stats.alerts.pendingChangeOrders} cambios pendientes</span>
            )}
            {stats.alerts.staleProjects > 0 && (
              <span>{stats.alerts.staleProjects} proyectos sin avance</span>
            )}
          </div>
        </div>
      )}

      <section className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-900">Proyectos recientes</h3>
          <Link to="/gestion/proyectos" className="text-xs text-primary font-semibold">
            Ver todos →
          </Link>
        </div>
        {stats.recentProjects.length === 0 ? (
          <p className="text-sm text-slate-500">Sin proyectos registrados</p>
        ) : (
          <ul className="space-y-3">
            {stats.recentProjects.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/gestion/proyectos/${p.id}`}
                  className="block active:bg-slate-50 rounded-lg -mx-2 px-2 py-2"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{p.name}</p>
                    <StatusBadge status={p.status} />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="truncate">{p.clientName}</span>
                    <span>·</span>
                    <span>{p.progress}% avance</span>
                    <span>·</span>
                    <span>{formatCurrency(p.budget)}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {stats.recentActivity.length > 0 && (
        <section className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="text-sm font-bold text-slate-900 mb-3">Actividad reciente</h3>
          <ul className="space-y-2.5">
            {stats.recentActivity.slice(0, 5).map((a) => (
              <li key={a.id} className="flex gap-2.5 text-xs">
                <span>📌</span>
                <div className="min-w-0">
                  <p className="text-slate-800 font-medium truncate">{a.action}</p>
                  <p className="text-slate-400">
                    {a.userName} · {timeAgo(a.timestamp)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ProjectLeadDashboard() {
  const [advances, setAdvances] = useState<Advance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    advanceService
      .getAdvances()
      .then(setAdvances)
      .catch((e) => setError(e.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <LoadingState message="Cargando dashboard…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const pending = advances.filter((a) => a.status === 'pending');
  const approved = advances.filter((a) => a.status === 'approved');
  const avgProgress =
    advances.length > 0
      ? Math.round(advances.reduce((s, a) => s + (a.progress_percentage || 0), 0) / advances.length)
      : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon="📊" value={`${avgProgress}%`} label="Avance promedio" />
        <StatCard
          icon="⏳"
          value={pending.length}
          label="Avances por aprobar"
          accent="text-amber-600"
        />
        <StatCard
          icon="✅"
          value={approved.length}
          label="Avances aprobados"
          accent="text-emerald-600"
        />
        <StatCard icon="📋" value={advances.length} label="Total avances" />
      </div>

      <section className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-900">Avances por aprobar</h3>
          <Link to="/gestion/avances" className="text-xs text-primary font-semibold">
            Ver todos →
          </Link>
        </div>
        {pending.length === 0 ? (
          <p className="text-sm text-slate-500">Todo al día 🎉</p>
        ) : (
          <ul className="space-y-3">
            {pending.slice(0, 5).map((a) => (
              <li key={a.id}>
                <Link
                  to={`/gestion/avances/${a.id}`}
                  className="block active:bg-slate-50 rounded-lg -mx-2 px-2 py-2"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {a.project_name || a.project_id.slice(0, 8)}
                    </p>
                    <StatusBadge status={a.status} />
                  </div>
                  <p className="text-xs text-slate-500">
                    Semana {a.week_number} · {a.progress_percentage}% ·{' '}
                    {a.contractor_name || 'Sin contratista'}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Link
        to="/gestion/avance-obra"
        className="block w-full py-3.5 rounded-xl bg-primary text-white text-center font-semibold shadow-sm active:bg-primary-dark"
      >
        + Registrar avance
      </Link>
    </div>
  );
}

function AccountantDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    adminDashboardService
      .getStats()
      .then(setStats)
      .catch((e) => setError(e.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!stats) return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          icon="💰"
          value={formatCurrency(stats.monthlyRevenue)}
          label="Ingresos del mes"
        />
        <StatCard
          icon="⏳"
          value={stats.alerts.overdueInvoices}
          label="Facturas vencidas"
          accent="text-red-600"
        />
        <StatCard
          icon="🔔"
          value={stats.alerts.pendingChangeOrders}
          label="Cambios pendientes"
          accent="text-amber-600"
        />
        <StatCard icon="📊" value={`${stats.averageProgress}%`} label="Avance promedio" />
      </div>

      <Link
        to="/gestion/finanzas"
        className="block w-full py-3.5 rounded-xl bg-primary text-white text-center font-semibold shadow-sm active:bg-primary-dark"
      >
        Ir a Finanzas
      </Link>
    </div>
  );
}

function ReceptionistDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    adminDashboardService
      .getStats()
      .then(setStats)
      .catch((e) => setError(e.message || 'Error al cargar'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!stats) return null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon="👥" value={stats.totalClients} label="Clientes registrados" />
        <StatCard icon="🏗️" value={stats.activeProjects} label="Proyectos activos" />
      </div>

      <Link
        to="/gestion/clientes"
        className="block w-full py-3.5 rounded-xl bg-primary text-white text-center font-semibold shadow-sm active:bg-primary-dark"
      >
        Ir a Clientes
      </Link>
    </div>
  );
}

function DataEntryDashboard() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon="📥" value="—" label="Capturas del día" />
        <StatCard icon="📋" value="—" label="Registros pendientes" />
      </div>

      <div className="grid grid-cols-1 gap-3">
        <Link
          to="/gestion/captura-manual"
          className="block w-full py-3.5 rounded-xl bg-primary text-white text-center font-semibold shadow-sm active:bg-primary-dark"
        >
          Ir a Captura Manual
        </Link>
        <Link
          to="/gestion/migracion"
          className="block w-full py-3.5 rounded-xl bg-slate-200 text-slate-800 text-center font-semibold shadow-sm active:bg-slate-300"
        >
          Ir a Migración ETL
        </Link>
      </div>
    </div>
  );
}

export function InternalDashboard() {
  const { user } = useAuth();
  const { pendingCount } = useOfflineSync();

  if (!user) return null;

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Buenos días';
    if (h < 19) return 'Buenas tardes';
    return 'Buenas noches';
  })();

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="bg-gradient-to-br from-primary to-primary-dark text-white rounded-2xl p-5 shadow-sm">
        <p className="text-sm text-white/80">{greeting},</p>
        <h2 className="text-xl font-bold leading-tight">{user.name}</h2>
        <p className="text-xs text-white/70 mt-1">{getRoleLabel(user.role)}</p>

        {pendingCount > 0 && (
          <div className="mt-3 inline-flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-xs font-medium">
              {pendingCount} pendiente{pendingCount === 1 ? '' : 's'} de sincronizar
            </span>
          </div>
        )}
      </div>

      {user.role === 'sys_admin' || user.role === 'general_manager' ? (
        <AdminGM role={user.role} />
      ) : user.role === 'project_lead' ? (
        <ProjectLeadDashboard />
      ) : user.role === 'accountant' ? (
        <AccountantDashboard />
      ) : user.role === 'receptionist' ? (
        <ReceptionistDashboard />
      ) : user.role === 'data_entry' ? (
        <DataEntryDashboard />
      ) : (
        <EmptyState
          icon="🚧"
          title="Dashboard en construcción"
          description="Tu rol aún no tiene un dashboard específico."
        />
      )}
    </div>
  );
}